import React, {
    DetailedHTMLProps,
    forwardRef,
    HTMLAttributes,
    useMemo,
} from "react";
import cn from "classnames";
import { Icon, IconProps } from "@evidenceb/athena-common/design-system/Icon";
import { v4 as uuid } from "uuid";

import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import capitalize from "lodash/capitalize";

import "./CustomSelect.scss";

export interface CustomSelectProps
    extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    onSelectOption: (value: string) => void;
    /** will be displayed in the custom select when selected */
    selectedOptionLabel:
        | string
        | React.ReactNode
        | (string | React.ReactNode)[];
    selectedOptionClassName?: string;
    /** indicated to the system select which option is selected (should be the same as the value prop of the option) */
    selectedOptionValue: string | number | undefined;
    label?: string;
    /** @default false */
    hideLabel?: boolean;
    iconProps?: IconProps;
    variant?: "default" | "inline" | "label-on-border";
    color?: string;
    /** @default "light" */
    colorMode?: "dark" | "light";
}

/**
 * Select component using native select functionalities but adding custom
 * styles.
 * Children should be the various options.
 */
const CustomSelect = forwardRef<HTMLSelectElement, CustomSelectProps>(
    (
        {
            onSelectOption,
            selectedOptionLabel,
            selectedOptionValue,
            label,
            iconProps,
            className,
            children,
            variant = "default",
            hideLabel = false,
            color,
            colorMode = "light",
            selectedOptionClassName,
            id,
        },
        ref
    ) => {
        const _id = useMemo<string>(() => uuid(), []);
        const actualId = id ?? _id;

        return (
            <div
                className={cn(
                    className,
                    "ds-custom-select",
                    `ds-custom-select--${variant}`,
                    `ds-custom-select--${colorMode}`
                )}
                /* @ts-ignore */
                style={{ "--accent-color": color }}
            >
                {label && !hideLabel && (
                    <label htmlFor={actualId}>{capitalize(label)}</label>
                )}
                {label && hideLabel && (
                    <VisuallyHidden>
                        <label htmlFor={actualId}>{capitalize(label)}</label>
                    </VisuallyHidden>
                )}

                <div className="ds-custom-select__select-wrapper">
                    <select
                        id={actualId}
                        className="ds-custom-select__native-select"
                        onChange={(e) => {
                            onSelectOption(e.target.value);
                        }}
                        ref={ref}
                        value={selectedOptionValue}
                    >
                        {children}
                    </select>

                    <div
                        className={cn(
                            "ds-custom-select__visual-select__wrapper",
                            selectedOptionClassName
                        )}
                    >
                        <div className="option">{selectedOptionLabel}</div>
                        <div className="visual-select__drop-icon">
                            {iconProps ? (
                                <Icon {...iconProps} />
                            ) : (
                                <Icon
                                    path="chevron_down"
                                    color={"var(--text-main-default)"}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

export default CustomSelect;
